import { APIClientServices } from "./APIClientServices";

// --- DTOs ---

export type SeniorityLevel = "junior" | "pleno" | "senior";

export interface PricingModelDTO {
	junior?: number;
	pleno?: number;
	senior?: number;
	[key: string]: number | undefined;
}

export type ToolENVType = "number" | "string" | "boolean" | "json" | `OAuth2:${string}`;

export interface ToolENV {
	envName: string;
	type: ToolENVType;
	label: string;
	description: string;
	required: boolean;
}

export interface PublicMcpDTO {
	id: number;
	slug: string;
	name: string;
	description: string | null;
	category: string;
	iconUrl: string | null;
	systemPrompt: string;
	mcpServerConfig: Record<string, any>;
	toolEnvFields: ToolENV[];
	pricingModel: PricingModelDTO;
	deliverableTypes: string[];
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface CreateMcpPayload {
	slug: string;
	name: string;
	system_prompt: string;
	description?: string;
	category?: string;
	icon_url?: string;
	mcp_server_config?: Record<string, any>;
	tool_env_fields?: ToolENV[];
	pricing_model?: PricingModelDTO;
	deliverable_types?: string[];
}

export interface UpdateMcpPayload {
	name?: string;
	description?: string;
	category?: string;
	icon_url?: string;
	system_prompt?: string;
	mcp_server_config?: Record<string, any>;
	tool_env_fields?: ToolENV[];
	pricing_model?: PricingModelDTO;
	deliverable_types?: string[];
	is_active?: boolean;
}

// --- Service ---

export class McpLibraryServices {
	private static readonly BASE_ROUTE = "v1/backoffice/mcps";
	private static readonly PUBLIC_ROUTE = "v1/mcps";

	/** Lista todos os MCPs (rota pública autenticada). Retorna apenas ativos. */
	static async listMcps(category?: string): Promise<PublicMcpDTO[]> {
		const params: Record<string, string> = {};
		if (category) params.category = category;
		return APIClientServices.get<PublicMcpDTO[]>(this.PUBLIC_ROUTE, params);
	}

	/** Busca um MCP por slug (rota pública autenticada). */
	static async getMcpBySlug(slug: string): Promise<PublicMcpDTO> {
		return APIClientServices.get<PublicMcpDTO>(`${this.PUBLIC_ROUTE}/${slug}`);
	}

	/** Cria um novo MCP (backoffice, requer MANAGER). */
	static async createMcp(data: CreateMcpPayload): Promise<PublicMcpDTO> {
		return APIClientServices.post<PublicMcpDTO>(this.BASE_ROUTE, data);
	}

	/** Atualiza um MCP existente (backoffice, requer MANAGER). */
	static async updateMcp(id: number, data: UpdateMcpPayload): Promise<PublicMcpDTO> {
		return APIClientServices.put<PublicMcpDTO>(`${this.BASE_ROUTE}/${id}`, data);
	}

	/** Desativa (soft delete) um MCP (backoffice, requer MANAGER). */
	static async deleteMcp(id: number): Promise<{ message: string }> {
		return APIClientServices.delete<{ message: string }>(`${this.BASE_ROUTE}/${id}`);
	}
}
